/**
 * Agent Service — Frontend API Client
 *
 * TypeScript client for the server-side agent architecture.
 * Handles session management, messaging, SSE streaming,
 * supervisor responses, workspace file access, and handoff.
 */

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : 'http://localhost:7777/api';

const AGENT_API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api/v1/agent'
  : 'http://localhost:7777/api/v1/agent';

// ============================================
// Types
// ============================================

export interface AgentSession {
  id: string;
  session_id: string | null;
  schedule_id?: string | null;
  trigger_type?: string | null;
  trigger_event?: string | null;
  user_email: string;
  model: string;
  reasoning_effort?: string;
  status: 'active' | 'paused' | 'completed' | 'failed' | 'stopped';
  execution_mode?: 'legacy' | 'local' | 'ecs';
  runner_status?: 'queued' | 'claimed' | 'starting' | 'running' | 'sticky' | 'idle' | 'failed' | 'stopped' | null;
  runner_sticky_until?: string | null;
  runner_started_at?: string | null;
  runner_finished_at?: string | null;
  workspace_path: string;
  total_input_tokens: number;
  total_output_tokens: number;
  total_cost_usd: number;
  title: string | null;
  created_at: string;
  updated_at: string;
  messages?: AgentMessage[];
}

export interface ListAgentSessionsOptions {
  episodeId?: string;
  scheduleId?: string;
  triggerType?: string;
  model?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
}

export interface AgentSessionListPage {
  sessions: AgentSession[];
  pagination?: {
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface AgentMessage {
  id: number;
  agent_session_id: string;
  role: 'user' | 'assistant' | 'tool_result';
  content: string;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  created_at: string;
}

export interface WorkspaceFile {
  path: string;
  size: number;
}

export interface ModelInfo {
  id: string;
  name: string;
  description: string;
  tier: 'opus' | 'sonnet';
}

export interface ModelVersions {
  opus: string;
  sonnet: string;
  models: ModelInfo[];
}

const FALLBACK_MODEL_VERSIONS: ModelVersions = {
  opus: 'opus',
  sonnet: 'sonnet',
  models: [
    { id: 'opus', name: 'Opus', description: 'Most intelligent', tier: 'opus' },
    { id: 'sonnet', name: 'Sonnet', description: 'Fast & capable', tier: 'sonnet' },
  ],
};

export interface SSEEvent {
  type: 'connected' | 'text' | 'text_delta' | 'status' | 'tool_start' | 'tool_end' | 'ask_supervisor' | 'cost_update' | 'done' | 'complete' | 'error' | 'ping' | 'session_update' | 'workspace_updated' | 'comments_updated' | 'ttml_updated';
  [key: string]: any;
}

export interface SupervisorQuestion {
  id: string;
  question: string;
  options: string[];
  context: string;
}

export interface HandoffPayload {
  agentSessionId: string;
  episodeId: string | null;
  model: string;
  systemPrompt: string;
  conversationHistory: any[];
  workspaceFiles: { path: string; size: number; downloadUrl: string }[];
  costSoFar: { inputTokens: number; outputTokens: number; costUsd: number };
  instructions: string;
}

// ============================================
// Auth Helper
// ============================================

function getAuthHeaders(authToken?: string, includeContentType = true): HeadersInit {
  const headers: HeadersInit = {};
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }
  return headers;
}

// ============================================
// Session Management
// ============================================

export async function createSession(sessionId?: string, model?: string, reasoningEffort?: string, authToken?: string): Promise<AgentSession> {
  const res = await fetch(`${AGENT_API_BASE_URL}/sessions`, {
    method: 'POST',
    credentials: 'include',
    headers: getAuthHeaders(authToken),
    body: JSON.stringify({ episodeId: sessionId, model, reasoningEffort }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

function buildSessionListParams(options: ListAgentSessionsOptions = {}): URLSearchParams {
  const params = new URLSearchParams();
  if (options.episodeId) params.set('episodeId', options.episodeId);
  if (options.scheduleId) params.set('scheduleId', options.scheduleId);
  if (options.triggerType) params.set('triggerType', options.triggerType);
  if (options.model) params.set('model', options.model);
  if (options.dateFrom) params.set('dateFrom', options.dateFrom);
  if (options.dateTo) params.set('dateTo', options.dateTo);
  if (options.limit != null) params.set('limit', String(options.limit));
  if (options.offset != null) params.set('offset', String(options.offset));
  return params;
}

export async function listSessionsPage(options: ListAgentSessionsOptions = {}, authToken?: string): Promise<AgentSessionListPage> {
  const params = buildSessionListParams(options);
  const res = await fetch(`${AGENT_API_BASE_URL}/sessions?${params}`, {
    credentials: 'include',
    headers: getAuthHeaders(authToken),
  });
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json();
  return {
    sessions: data.sessions || [],
    pagination: data.pagination || undefined,
  };
}

export async function listSessions(sessionId?: string, authToken?: string): Promise<AgentSession[]> {
  const page = await listSessionsPage(sessionId ? { episodeId: sessionId } : {}, authToken);
  return page.sessions;
}

export async function getSession(agentSessionId: string, authToken?: string): Promise<AgentSession> {
  const res = await fetch(`${AGENT_API_BASE_URL}/sessions/${agentSessionId}`, {
    credentials: 'include',
    headers: getAuthHeaders(authToken),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function deleteSession(agentSessionId: string, authToken?: string): Promise<void> {
  const res = await fetch(`${AGENT_API_BASE_URL}/sessions/${agentSessionId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: getAuthHeaders(authToken),
  });
  if (!res.ok) throw new Error(await res.text());
}

// ============================================
// Messaging
// ============================================

export interface SendMessageResult {
  ok: boolean;
  queued?: boolean;
  queueLength?: number;
}

export async function sendMessage(agentSessionId: string, message: string, authToken?: string): Promise<SendMessageResult> {
  const res = await fetch(`${AGENT_API_BASE_URL}/sessions/${agentSessionId}/messages`, {
    method: 'POST',
    credentials: 'include',
    headers: getAuthHeaders(authToken),
    body: JSON.stringify({ message }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getMessages(agentSessionId: string, authToken?: string): Promise<AgentMessage[]> {
  const res = await fetch(`${AGENT_API_BASE_URL}/sessions/${agentSessionId}/messages`, {
    credentials: 'include',
    headers: getAuthHeaders(authToken),
  });
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json();
  return data.messages || [];
}

// ============================================
// Supervisor Interaction
// ============================================

export async function respondToSupervisor(
  agentSessionId: string,
  questionId: string,
  response: string,
  authToken?: string,
): Promise<void> {
  const res = await fetch(`${AGENT_API_BASE_URL}/sessions/${agentSessionId}/respond`, {
    method: 'POST',
    credentials: 'include',
    headers: getAuthHeaders(authToken),
    body: JSON.stringify({ questionId, response }),
  });
  if (!res.ok) throw new Error(await res.text());
}

export async function stopSession(agentSessionId: string, authToken?: string): Promise<void> {
  const res = await fetch(`${AGENT_API_BASE_URL}/sessions/${agentSessionId}/stop`, {
    method: 'POST',
    credentials: 'include',
    headers: getAuthHeaders(authToken),
  });
  if (!res.ok) throw new Error(await res.text());
}

export async function finishSession(agentSessionId: string, authToken?: string): Promise<void> {
  const res = await fetch(`${AGENT_API_BASE_URL}/sessions/${agentSessionId}/finish`, {
    method: 'POST',
    credentials: 'include',
    headers: getAuthHeaders(authToken),
  });
  if (!res.ok) throw new Error(await res.text());
}

export async function setCostLimit(agentSessionId: string, costLimitUsd: number, authToken?: string): Promise<{ costLimitUsd: number }> {
  const res = await fetch(`${API_BASE_URL}/agent/sessions/${agentSessionId}/cost-limit`, {
    method: 'PUT',
    credentials: 'include',
    headers: getAuthHeaders(authToken),
    body: JSON.stringify({ costLimitUsd }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function updateRuntime(
  agentSessionId: string,
  updates: { model?: string; reasoningEffort?: string },
  authToken?: string,
): Promise<{ id: string; model: string; reasoningEffort: string }> {
  const res = await fetch(`${API_BASE_URL}/agent/sessions/${agentSessionId}/runtime`, {
    method: 'PUT',
    credentials: 'include',
    headers: getAuthHeaders(authToken),
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// ============================================
// Workspace Files
// ============================================

export async function getWorkspaceFiles(agentSessionId: string, authToken?: string): Promise<WorkspaceFile[]> {
  const res = await fetch(`${AGENT_API_BASE_URL}/sessions/${agentSessionId}/files`, {
    credentials: 'include',
    headers: getAuthHeaders(authToken),
  });
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json();
  return data.files || [];
}

export async function getWorkspaceFile(agentSessionId: string, filePath: string, authToken?: string): Promise<Blob> {
  const res = await fetch(`${AGENT_API_BASE_URL}/sessions/${agentSessionId}/files/${filePath}`, {
    credentials: 'include',
    headers: getAuthHeaders(authToken),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.blob();
}

export interface UploadedFile {
  path: string;
  size: number;
  originalName: string;
}

export async function uploadFiles(
  agentSessionId: string,
  files: File[],
  subdir?: string,
  authToken?: string,
): Promise<UploadedFile[]> {
  const formData = new FormData();
  for (const file of files) {
    formData.append('files', file);
  }
  const params = subdir ? `?subdir=${encodeURIComponent(subdir)}` : '';
  const res = await fetch(`${AGENT_API_BASE_URL}/sessions/${agentSessionId}/files${params}`, {
    method: 'POST',
    credentials: 'include',
    headers: getAuthHeaders(authToken, false),
    body: formData,
  });
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json();
  return data.uploaded;
}

// ============================================
// SSE Stream
// ============================================

export function connectStream(
  agentSessionId: string,
  onEvent: (event: SSEEvent) => void,
  onError?: (err: Event) => void,
  authToken?: string,
): EventSource {
  const params = new URLSearchParams();
  if (authToken) params.set('authToken', authToken);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  const url = `${AGENT_API_BASE_URL}/sessions/${agentSessionId}/stream${suffix}`;
  const es = new EventSource(url, { withCredentials: true });

  es.onmessage = (e) => {
    try {
      const event: SSEEvent = JSON.parse(e.data);
      onEvent(event);
    } catch {
      // Ignore malformed events
    }
  };

  es.onerror = (e) => {
    if (onError) onError(e);
  };

  return es;
}

// ============================================
// Model Info
// ============================================

function normalizeModelVersions(raw: any): ModelVersions {
  const models = Array.isArray(raw?.models)
    ? raw.models
        .map((model: any) => {
          const id = typeof model?.id === 'string' ? model.id.trim() : '';
          const tier = model?.tier === 'opus' || model?.tier === 'sonnet' ? model.tier : null;
          if (!id || !tier) return null;
          return {
            id,
            name: typeof model?.name === 'string' && model.name.trim()
              ? model.name.trim()
              : tier === 'opus' ? 'Opus' : 'Sonnet',
            description: typeof model?.description === 'string' ? model.description : '',
            tier,
          } satisfies ModelInfo;
        })
        .filter(Boolean) as ModelInfo[]
    : [];

  if (models.length > 0) {
    return {
      opus: typeof raw?.opus === 'string' && raw.opus.trim()
        ? raw.opus.trim()
        : models.find((model) => model.tier === 'opus')?.id || FALLBACK_MODEL_VERSIONS.opus,
      sonnet: typeof raw?.sonnet === 'string' && raw.sonnet.trim()
        ? raw.sonnet.trim()
        : models.find((model) => model.tier === 'sonnet')?.id || FALLBACK_MODEL_VERSIONS.sonnet,
      models,
    };
  }

  const opus = typeof raw?.opus === 'string' && raw.opus.trim() ? raw.opus.trim() : FALLBACK_MODEL_VERSIONS.opus;
  const sonnet = typeof raw?.sonnet === 'string' && raw.sonnet.trim() ? raw.sonnet.trim() : FALLBACK_MODEL_VERSIONS.sonnet;

  return {
    opus,
    sonnet,
    models: [
      { id: opus, name: 'Opus', description: 'Most intelligent', tier: 'opus' },
      { id: sonnet, name: 'Sonnet', description: 'Fast & capable', tier: 'sonnet' },
    ],
  };
}

export async function getLatestModels(authToken?: string): Promise<ModelVersions> {
  const endpoints = [
    `${API_BASE_URL}/v1/models`,
    `${AGENT_API_BASE_URL}/models/latest`,
    `${AGENT_API_BASE_URL}/models`,
  ];

  let lastError: Error | null = null;
  for (const url of endpoints) {
    try {
      const res = await fetch(url, {
        credentials: 'include',
        headers: getAuthHeaders(authToken, false),
      });
      if (!res.ok) {
        throw new Error(await res.text());
      }
      return normalizeModelVersions(await res.json());
    } catch (error: any) {
      lastError = error instanceof Error ? error : new Error(String(error));
    }
  }

  if (lastError) {
    console.error('[agentService] Failed to load latest models:', lastError);
  }
  return FALLBACK_MODEL_VERSIONS;
}

// ============================================
// Agent-Based Refinement Sessions
// ============================================

export async function startRefinementSession(options: {
  sessionId: string;
  model?: string;
  additionalInstructions?: string;
  enableWebSearch?: boolean;
}, authToken?: string): Promise<{ success: boolean; agentSessionId: string; model: string }> {
  const res = await fetch(`${API_BASE_URL}/v1/agent/workflows/transcript-refinement`, {
    method: 'POST',
    credentials: 'include',
    headers: getAuthHeaders(authToken),
    body: JSON.stringify(options),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function startTtmlRefinementSession(options: {
  sessionId: string;
  sourceChecksum: string;
  model?: string;
  additionalInstructions?: string;
  enableWebAccess?: boolean;
  iterationCount?: number;
  blankSlate?: boolean;
  maxIssuesToFix?: number;
  topicDensity?: number;
}, authToken?: string): Promise<{ success: boolean; agentSessionId: string; model: string }> {
  const res = await fetch(`${API_BASE_URL}/v1/agent/workflows/metadata-revision`, {
    method: 'POST',
    credentials: 'include',
    headers: getAuthHeaders(authToken),
    body: JSON.stringify(options),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function startTtmlGenerationSession(options: {
  sessionId: string;
  model?: string;
  additionalInstructions?: string;
  enableWebAccess?: boolean;
  enableWebSearch?: boolean;
  topicDensity?: number;
  includeEdits?: boolean;
}, authToken?: string): Promise<{ success: boolean; agentSessionId: string; model: string }> {
  const res = await fetch(`${API_BASE_URL}/v1/agent/workflows/metadata-generation`, {
    method: 'POST',
    credentials: 'include',
    headers: getAuthHeaders(authToken),
    body: JSON.stringify(options),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function startNarrativeGenerationSession(options: {
  sessionId: string;
  model?: string;
  reasoningEffort?: string;
  additionalInstructions?: string;
}, authToken?: string): Promise<{ success: boolean; agentSessionId: string; model: string }> {
  const res = await fetch(`${API_BASE_URL}/v1/agent/workflows/topic-map`, {
    method: 'POST',
    credentials: 'include',
    headers: getAuthHeaders(authToken),
    body: JSON.stringify(options),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function startTitleGenerationSession(options: {
  sessionId: string;
  model?: string;
  reasoningEffort?: string;
  additionalInstructions?: string;
}, authToken?: string): Promise<{ success: boolean; agentSessionId: string; model: string }> {
  const res = await fetch(`${API_BASE_URL}/v1/agent/workflows/title-generation`, {
    method: 'POST',
    credentials: 'include',
    headers: getAuthHeaders(authToken),
    body: JSON.stringify(options),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function startDescriptionCompressionSession(options: {
  sessionId: string;
  description: string;
  toc?: string;
  references?: string;
  targetLength?: number;
  model?: string;
  reasoningEffort?: string;
  additionalInstructions?: string;
}, authToken?: string): Promise<{ success: boolean; agentSessionId: string; model: string }> {
  const res = await fetch(`${API_BASE_URL}/v1/agent/workflows/description-compression`, {
    method: 'POST',
    credentials: 'include',
    headers: getAuthHeaders(authToken),
    body: JSON.stringify(options),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function startSpeakerInferenceSession(options: {
  sessionId: string;
  model?: string;
  reasoningEffort?: string;
  additionalInstructions?: string;
  enableWebSearch?: boolean;
}, authToken?: string): Promise<{ success: boolean; agentSessionId: string; model: string }> {
  const res = await fetch(`${API_BASE_URL}/v1/agent/workflows/speaker-inference`, {
    method: 'POST',
    credentials: 'include',
    headers: getAuthHeaders(authToken),
    body: JSON.stringify(options),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// ============================================
// BYOK (Bring Your Own Key)
// ============================================

export interface ApiKeyStatus {
  hasKey: boolean;
  keyPreview: string | null;
}

export async function getApiKeyStatus(authToken?: string): Promise<ApiKeyStatus> {
  const res = await fetch(`${API_BASE_URL}/agent/api-key`, {
    credentials: 'include',
    headers: getAuthHeaders(authToken),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function setApiKey(apiKey: string, authToken?: string): Promise<{ hasKey: boolean }> {
  const res = await fetch(`${API_BASE_URL}/agent/api-key`, {
    method: 'PUT',
    credentials: 'include',
    headers: getAuthHeaders(authToken),
    body: JSON.stringify({ apiKey }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// ============================================
// Handoff (Claude Code)
// ============================================

export async function getHandoff(agentSessionId: string, authToken?: string): Promise<HandoffPayload> {
  const res = await fetch(`${AGENT_API_BASE_URL}/sessions/${agentSessionId}/handoff`, {
    credentials: 'include',
    headers: getAuthHeaders(authToken),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// ============================================
// Default Export
// ============================================

const agentService = {
  createSession,
  listSessions,
  getSession,
  deleteSession,
  sendMessage,
  getMessages,
  respondToSupervisor,
  stopSession,
  setCostLimit,
  updateRuntime,
  getWorkspaceFiles,
  getWorkspaceFile,
  uploadFiles,
  connectStream,
  getLatestModels,
  startRefinementSession,
  startTtmlRefinementSession,
  startTtmlGenerationSession,
  startNarrativeGenerationSession,
  startTitleGenerationSession,
  startSpeakerInferenceSession,
  getApiKeyStatus,
  setApiKey,
  getHandoff,
};

export default agentService;
